import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  aplicarRecompensasAlPersonaje,
  ejecutarAccionPlayer,
  iniciarCombate,
  type CombatState,
  type PlayerAction,
} from '@/engine/combat';
import { useMetaStore } from '@/engine/state';
import { EnemyPortrait } from './EnemyPortrait';
import { CombatLog } from './CombatLog';
import { ActionBar } from './ActionBar';
import { StatBar, Button } from '@/components/ui';

interface Props {
  enemyId: string;
  onSalir: (resultado: 'victoria' | 'derrota' | 'huida') => void;
}

export function CombatScreen({ enemyId, onSalir }: Props) {
  const [state, setState] = useState<CombatState>(() => iniciarCombate(enemyId));
  const [shake, setShake] = useState(false);
  const aplicadoRef = useRef(false);
  const setCombateActivo = useMetaStore((s) => s.setCombateActivo);

  // Suprimir el Game Over por muerte mientras el combate está activo;
  // al salir lo reactivamos.
  useEffect(() => {
    setCombateActivo(true);
    return () => setCombateActivo(false);
  }, [setCombateActivo]);

  const deshabilitado = state.estado !== 'activo' || state.turno !== 'player';

  const ejecutar = (a: PlayerAction) => {
    setState((prev) => ejecutarAccionPlayer(prev, a));
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  useEffect(() => {
    if (state.estado !== 'activo' && !aplicadoRef.current) {
      aplicadoRef.current = true;
      aplicarRecompensasAlPersonaje(state);
    }
  }, [state]);

  const resultado = useMemo(() => {
    switch (state.estado) {
      case 'victoria':
        return {
          titulo: 'Victoria',
          subtitulo: state.xpGanada ? `+${state.xpGanada} XP` : '',
          sub: `Venciste a ${state.enemy.nombre}.`,
        };
      case 'derrota':
        return {
          titulo: 'Caíste',
          subtitulo: '',
          sub: `${state.enemy.nombre} te dejó en el suelo.`,
        };
      case 'huida':
        return { titulo: 'Escapaste', subtitulo: '', sub: 'Te zafaste.' };
      default:
        return null;
    }
  }, [state]);

  return (
    // Fixed + z-alto: cubre TODO (incluida la navegación lateral y la barra
    // inferior) para que no se pueda salir accidentalmente del combate y
    // reiniciarlo. La única salida es vía `onSalir` cuando el combate termina.
    <div className="fixed inset-0 z-40 overflow-hidden bg-ash-950">
      <div className="absolute inset-0 bg-gradient-to-b from-ash-950 via-ember-950/30 to-ash-950" aria-hidden />
      <div className="absolute inset-0 bg-vignette" aria-hidden />

      <div className="relative z-10 h-full w-full flex flex-col min-h-0 min-w-0 px-[clamp(0.75rem,2.5vmin,2rem)] py-[clamp(0.5rem,2vmin,1.25rem)] gap-3">
        <div className="shrink-0 grid grid-cols-[1fr_1.3fr] md:grid-cols-[1fr_1.4fr] gap-3 min-h-0">
          <div className="flex items-center justify-center min-w-0">
            <EnemyPortrait enemy={state.enemy} shake={shake && state.turno === 'player'} />
          </div>
          <div className="rounded border border-ash-800 bg-ash-950/60 p-3 md:p-4 space-y-2.5 min-w-0">
            <h2 className="font-display fluid-base text-ash-100 truncate">Franco G.</h2>
            <StatBar
              label="Vida"
              valor={state.player.vida}
              max={state.player.vidaMax}
              color="ember"
            />
            <StatBar
              label="Mana"
              valor={state.player.mana}
              max={state.player.manaMax}
              color="mystic"
            />
            <p className="fluid-xs text-ash-500 font-ui uppercase tracking-widest">
              Ronda {state.ronda} ·{' '}
              {state.turno === 'player' ? 'tu turno' : state.enemy.nombre}
            </p>
            {state.player.efectosTemporales.length > 0 && (
              <p className="fluid-xs text-moss-300 font-ui clamp-2">
                Efectos: {state.player.efectosTemporales.map((e) => e.descripcion).join(', ')}
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <CombatLog entries={state.log} />
        </div>

        <div className="shrink-0">
          <ActionBar onAction={ejecutar} deshabilitado={deshabilitado} player={state.player} />
        </div>

        <AnimatePresence>
          {resultado && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-ash-950/85 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                className="w-full max-w-md text-center px-6 py-8 rounded-lg border border-ash-800 bg-ash-950"
              >
                <p className="fluid-xs uppercase tracking-[0.4em] text-ember-400 font-display mb-2">
                  {resultado.subtitulo}
                </p>
                <h2 className="text-display fluid-2xl text-ash-100 mb-2">
                  {resultado.titulo}
                </h2>
                <p className="text-narrative text-ash-300 mb-5 fluid-base">{resultado.sub}</p>
                <Button
                  variant="primary"
                  onClick={() =>
                    onSalir(state.estado === 'activo' ? 'huida' : state.estado)
                  }
                >
                  Continuar
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
